import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import useHttp from "../../../hooks/http.hook";
import {
  buildStatusFilterOptions,
  getProviderStatusLabel,
  sortCustomOrderStatuses,
  statusBadgeClassName,
} from "../../../types/OrderStatusTypes";
import { OrderTypes, isExpenseOrder, isInvestmentOrder, isReplenishOrder } from "../../../types/OrderTypes";
import { PaymentStatusTypes } from "../../../types/PaymentStatusTypes";
import DateRangeCalendarModal, {
  formatDateFilterLabel,
} from "../../../app/orders/components/DateRangeCalendarModal";
import OrderFilterSelectModal from "../../../app/orders/components/OrderFilterSelectModal";
import "../../../app/orders/OrdersPage.css";
import "./DashboardOrdersList.css";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const TYPE_FILTER_OPTIONS = [
  { id: OrderTypes.SALES, label: "продажи" },
  { id: OrderTypes.REPLENISH, label: "пополнения" },
  { id: OrderTypes.EXPENSES, label: "расходы" },
  { id: OrderTypes.INVESTMENT, label: "инвестиции" },
];
const VALID_TYPE_IDS = new Set(TYPE_FILTER_OPTIONS.map((item) => item.id));
const PAYMENT_LABELS = PaymentStatusTypes.labels;

function parseTypeFilterParam(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  return VALID_TYPE_IDS.has(n) ? n : OrderTypes.SALES;
}

function parseStatusIdsParam(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((id) => Number.parseInt(id.trim(), 10))
    .filter((id) => Number.isFinite(id));
}

function parseDateParam(value) {
  const raw = String(value ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

function applyOrdersFilterParams(prev, updates) {
  const next = new URLSearchParams(prev);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
      next.delete(key);
      return;
    }
    next.set(key, Array.isArray(value) ? value.join(",") : String(value));
  });
  if (next.get("type") === String(OrderTypes.SALES)) {
    next.delete("type");
  }
  if (!next.get("dateFrom")) {
    next.delete("dateTo");
  }
  return next;
}

function formatTypeFilterLabel(typeId) {
  const option = TYPE_FILTER_OPTIONS.find((item) => item.id === typeId) ?? TYPE_FILTER_OPTIONS[0];
  return `Тип операции (${option.label})`;
}

function formatStatusFilterLabel(statusIds, options) {
  if (!statusIds.length) return "Статусы";
  const labels = (options || [])
    .filter((item) => statusIds.includes(item.id))
    .map((item) => item.label);
  return `Статусы (${labels.join(", ")})`;
}

function formatTotalPrice(value) {
  const n = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(n)) return "0.00 сом";
  return `${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} сом`;
}

function getOrderDateKey(order) {
  const raw = order?.createdAt ?? order?.created_at;
  if (!raw) return "";
  const d = typeof raw === "string" ? new Date(raw) : raw;
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey) {
  if (!dateKey) return dateKey;
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isToday) return "Сегодня";
  if (isYesterday) return "Вчера";
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function groupOrdersByDate(orders) {
  const byDate = {};
  orders.forEach((order) => {
    const key = getOrderDateKey(order);
    if (!key) return;
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(order);
  });
  const keys = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
  return keys.map((key) => ({
    dateKey: key,
    dateLabel: formatDateLabel(key),
    orders: byDate[key],
  }));
}

/**
 * @param {{ scrollRoot: HTMLElement | null }} props
 */
function DashboardOrdersList({ scrollRoot }) {
  const { branchId, tgChannelId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requestWithMeta } = useHttp();

  const [orders, setOrders] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);
  const [customStatuses, setCustomStatuses] = React.useState([]);
  const [statusesReady, setStatusesReady] = React.useState(false);

  const selectedTypeId = parseTypeFilterParam(searchParams.get("type"));
  const selectedStatusKey = searchParams.get("statusIds") || "";
  const selectedStatusIds = React.useMemo(
    () => parseStatusIdsParam(selectedStatusKey),
    [selectedStatusKey]
  );
  const dateFrom = parseDateParam(searchParams.get("dateFrom"));
  const dateTo = parseDateParam(searchParams.get("dateTo")) || dateFrom;
  const searchQuery = String(searchParams.get("search") ?? "").trim();
  const [searchInput, setSearchInput] = React.useState(
    () => String(searchParams.get("search") ?? "")
  );

  const sentinelRef = React.useRef(null);
  const searchDebounceRef = React.useRef(null);

  const hasMore = page < totalPages;
  const dateGroups = React.useMemo(() => groupOrdersByDate(orders), [orders]);
  const statusFilterOptions = React.useMemo(
    () => buildStatusFilterOptions(customStatuses),
    [customStatuses]
  );
  const allStatusIds = React.useMemo(
    () => statusFilterOptions.map(({ id }) => id),
    [statusFilterOptions]
  );
  const dateFilterKey = `${dateFrom || ""}|${dateTo || ""}`;
  const dateFilterSelected = Boolean(dateFrom);
  const typeFilterSelected = selectedTypeId !== OrderTypes.SALES;
  const statusFilterSelected = selectedStatusIds.length > 0;
  const ordersSearch = searchParams.toString();

  const setFilterParams = React.useCallback((updates) => {
    setSearchParams((prev) => applyOrdersFilterParams(prev, updates), { replace: true });
  }, [setSearchParams]);

  const orderHref = React.useCallback(
    (orderId) =>
      `/operator/${encodeURIComponent(tgChannelId)}/branch/${encodeURIComponent(branchId)}/orders/${encodeURIComponent(orderId)}`,
    [branchId, tgChannelId]
  );

  const openOrder = (id) => {
    navigate({
      pathname: orderHref(id),
      search: ordersSearch,
    });
  };

  React.useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  React.useEffect(() => {
    setSearchInput((prev) => (prev === searchQuery ? prev : searchQuery));
  }, [searchQuery]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilterParams({ search: String(value ?? "").trim() });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchClear = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput("");
    setFilterParams({ search: "" });
  };

  const fetchPage = React.useCallback(
    async (pageNum) => {
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(PAGE_SIZE),
      });
      params.set("typeIds", String(selectedTypeId));
      if (selectedStatusIds.length > 0) {
        params.set("statusIds", selectedStatusIds.join(","));
      }
      if (dateFrom) {
        params.set("dateFrom", dateFrom);
        params.set("dateTo", dateTo || dateFrom);
      }
      if (searchQuery) {
        params.set("search", searchQuery);
      }
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/orders?${params.toString()}`,
        "GET"
      );
      if (!res?.ok) {
        return { ok: false, orders: [], pagination: null };
      }
      const list = Array.isArray(res?.data?.orders) ? res.data.orders : [];
      const pagination = res?.data?.pagination ?? {};
      return {
        ok: true,
        orders: list,
        totalPages: pagination.totalPages ?? 1,
      };
    },
    [branchId, requestWithMeta, selectedStatusIds, selectedTypeId, dateFrom, dateTo, searchQuery]
  );

  React.useEffect(() => {
    let cancelled = false;
    setStatusesReady(false);
    (async () => {
      try {
        const res = await requestWithMeta("/operator/custom-order-statuses", "GET");
        if (cancelled) return;
        const list = Array.isArray(res?.data?.customOrderStatuses)
          ? res.data.customOrderStatuses
          : [];
        setCustomStatuses(sortCustomOrderStatuses(list));
      } catch (_) {
        if (!cancelled) setCustomStatuses([]);
      } finally {
        if (!cancelled) setStatusesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWithMeta]);

  React.useEffect(() => {
    if (!branchId) return undefined;
    if (!statusesReady) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    setOrders([]);
    setTotalPages(1);

    (async () => {
      try {
        const result = await fetchPage(1);
        if (cancelled) return;
        if (!result.ok) {
          setError("Не удалось загрузить заказы");
          setOrders([]);
          return;
        }
        setOrders(result.orders);
        setTotalPages(result.totalPages);
      } catch (_) {
        if (!cancelled) {
          setError("Ошибка загрузки");
          setOrders([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [branchId, fetchPage, selectedStatusKey, selectedTypeId, dateFilterKey, searchQuery, statusesReady]);

  React.useEffect(() => {
    const root = scrollRoot;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [e] = entries;
        if (!e?.isIntersecting) return;
        if (loading || loadingMore || page >= totalPages) return;

        setLoadingMore(true);
        fetchPage(page + 1)
          .then((result) => {
            if (!result.ok) return;
            setOrders((prev) => [...prev, ...result.orders]);
            setPage((p) => p + 1);
            setTotalPages(result.totalPages);
          })
          .finally(() => setLoadingMore(false));
      },
      { root, rootMargin: "100px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollRoot, hasMore, loading, loadingMore, page, totalPages, fetchPage]);

  const handleApplyDateFilter = ({ start, end }) => {
    setFilterParams({
      dateFrom: start || null,
      dateTo: end || start || null,
    });
  };

  const handleApplyTypeFilter = (ids) => {
    setFilterParams({ type: ids[0] ?? OrderTypes.SALES });
  };

  const handleApplyStatusFilter = (ids) => {
    const unique = [...new Set(ids)];
    const isAll =
      unique.length === 0 ||
      (unique.length === allStatusIds.length && allStatusIds.every((id) => unique.includes(id)));
    setFilterParams({ statusIds: isAll ? [] : unique });
  };

  if (!branchId || !tgChannelId) return null;

  return (
    <section className="dash-orders" aria-label="Журнал заказов">
      <div className="orders-page-search">
        <svg
          className="orders-page-searchIcon"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path
            d="M20 20l-3.2-3.2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="search"
          className="orders-page-searchInput"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Имя или телефон"
          aria-label="Поиск по имени или телефону"
          autoComplete="off"
          enterKeyHint="search"
        />
        {searchInput ? (
          <button
            type="button"
            className="orders-page-searchClear"
            onClick={handleSearchClear}
            aria-label="Очистить поиск"
          >
            ×
          </button>
        ) : null}
      </div>

      <div
        className="orders-page-statusFilters"
        role="group"
        aria-label="Фильтр заказов"
      >
        <button
          type="button"
          className={`orders-page-statusFilterTag${dateFilterSelected ? " is-selected" : ""}`}
          aria-pressed={dateFilterSelected}
          onClick={() => setCalendarOpen(true)}
        >
          {formatDateFilterLabel(dateFrom, dateTo)}
        </button>
        <button
          type="button"
          className={`orders-page-statusFilterTag${typeFilterSelected ? " is-selected" : ""}`}
          aria-pressed={typeFilterSelected}
          onClick={() => setTypeModalOpen(true)}
        >
          {formatTypeFilterLabel(selectedTypeId)}
        </button>
        <button
          type="button"
          className={`orders-page-statusFilterTag${statusFilterSelected ? " is-selected" : ""}`}
          aria-pressed={statusFilterSelected}
          onClick={() => setStatusModalOpen(true)}
        >
          {formatStatusFilterLabel(selectedStatusIds, statusFilterOptions)}
        </button>
      </div>

      {loading ? (
        <div className="orders-page-loading" aria-busy="true">
          <div className="orders-page-skeleton orders-page-skeleton--block" />
          <div className="orders-page-skeleton orders-page-skeleton--block" />
          <div className="orders-page-skeleton orders-page-skeleton--block" />
        </div>
      ) : error ? (
        <div className="orders-page-error">{error}</div>
      ) : dateGroups.length === 0 ? (
        <div className="orders-page-empty">{searchQuery ? "Ничего не найдено" : "Нет заказов"}</div>
      ) : (
        <>
          {dateGroups.map((group) => (
            <section key={group.dateKey} className="orders-page-dateSection">
              <h3 className="orders-page-dateHeading">{group.dateLabel}</h3>
              <div className="orders-page-tableWrap">
                <table
                  className="orders-page-table"
                  aria-label={`Заказы за ${group.dateLabel}`}
                >
                  <thead>
                    <tr>
                      <th scope="col">Заказ</th>
                      <th scope="col" className="orders-page-colDesktop">
                        Клиент
                      </th>
                      <th scope="col" className="orders-page-colDesktop">
                        Сумма
                      </th>
                      <th scope="col" className="orders-page-colDesktop">
                        Оплата
                      </th>
                      <th scope="col">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.orders.map((order) => {
                      const id = order?.id ?? order?._id;
                      const statusId = order?.statusId ?? order?.status_id;
                      const statusLabel = getProviderStatusLabel(statusId, customStatuses);
                      const paymentStatusId =
                        order?.paymentStatusId ??
                        order?.payment_status_id ??
                        PaymentStatusTypes.NOT_PAID;
                      const isReplenishment = isReplenishOrder(order);
                      const isExpense = isExpenseOrder(order);
                      const isInvestment = isInvestmentOrder(order);
                      const isSystem = isReplenishment || isExpense || isInvestment;
                      const paymentLabel = isSystem
                        ? "—"
                        : (PAYMENT_LABELS[paymentStatusId] ?? `Оплата ${paymentStatusId}`);
                      const title = isReplenishment
                        ? `Пополнение № ${id}`
                        : isExpense
                          ? `Расходы № ${id}`
                          : isInvestment
                            ? `Инвестиция № ${id}`
                            : `Заказ № ${id}`;
                      const customerName = isSystem
                        ? "—"
                        : (order?.customerName ?? order?.customer_name ?? "—");
                      const totalLabel = isReplenishment
                        ? "—"
                        : formatTotalPrice(order?.totalPrice ?? order?.total_price);

                      return (
                        <tr
                          key={id}
                          className="orders-page-row"
                          tabIndex={0}
                          onClick={() => openOrder(id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openOrder(id);
                            }
                          }}
                        >
                          <td className="orders-page-itemId">{title}</td>
                          <td className="orders-page-colDesktop orders-page-itemCustomer">
                            {customerName}
                          </td>
                          <td className="orders-page-colDesktop orders-page-itemTotal">
                            {totalLabel}
                          </td>
                          <td className="orders-page-colDesktop">
                            {isSystem ? (
                              <span className="orders-page-itemPaymentMuted">—</span>
                            ) : (
                              <span
                                className={`orders-page-itemPayment orders-page-itemPayment--${paymentStatusId}`}
                              >
                                {paymentLabel}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={statusBadgeClassName("orders-page-itemStatus", statusId)}>
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          <div
            ref={sentinelRef}
            className="orders-page-sentinel"
            aria-hidden="true"
          />
          {loadingMore ? (
            <div className="orders-page-loadingMore" aria-busy="true">
              <div className="orders-page-skeleton orders-page-skeleton--block" />
              <div className="orders-page-skeleton orders-page-skeleton--block" />
            </div>
          ) : null}
        </>
      )}

      <DateRangeCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        initialStart={dateFrom}
        initialEnd={dateTo}
        onApply={handleApplyDateFilter}
      />
      <OrderFilterSelectModal
        isOpen={typeModalOpen}
        title="Тип операции"
        options={TYPE_FILTER_OPTIONS}
        selectedIds={[selectedTypeId]}
        resetIds={[OrderTypes.SALES]}
        onApply={handleApplyTypeFilter}
        onClose={() => setTypeModalOpen(false)}
      />
      <OrderFilterSelectModal
        isOpen={statusModalOpen}
        title="Статусы"
        options={statusFilterOptions}
        selectedIds={selectedStatusIds}
        multiple
        resetIds={allStatusIds}
        onApply={handleApplyStatusFilter}
        onClose={() => setStatusModalOpen(false)}
      />
    </section>
  );
}

export default DashboardOrdersList;
